import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { FormikHelpers, useFormik } from "formik";
import { Redirect, useHistory } from "react-router-dom";
import {Box, CircularProgress, Typography} from "@material-ui/core";
import ClearIcon from '@material-ui/icons/Clear';
import { Link } from "react-router-dom";

import useStyles from "./AdminSignIn.style";
import {remoteAuthUserRequest, useAuthenticationContext} from "../../context/UserContext";
import {AdminLoginRequestAttributes} from "../../attributes/user-attributes";
import {deviceInfo, parseJson} from "../../common/Hooks";
import UserService from "../../services/UserService";
import User from "../../models/User";
import {ICIPAdminScreen} from "../icip_admin_screen/ICIPAdminScreen";
import {PageTitle} from "../../components/page-title/PageTitle";
import {APP_NAME} from "../../settings/constants";
import {SlideWrapper} from "../../components/slide-wrapper/SlideWrapper";
import {LanguageChooser, LoginButton, InputStyled, InputAdornment, StyledIconButton} from "./components/all";
import {SignInScreen} from "../signin/SignInScreen";

interface AdminSignInProp {}

interface LoginState {
  showPassword: boolean;
}

const AdminSignIn = (props: AdminSignInProp) => {
  const classes = useStyles();
  const intl = useIntl();
  let history = useHistory();
  // Context
  const [_u, _t, authDispatch] = useAuthenticationContext();
  // State
  const [values, setValues] = useState<LoginState>({
    showPassword: false,
  });

  const handleClickShowPassword = () => {
    setValues({
      showPassword: !values.showPassword,
    });
  };

  const handleClearInput = (key: string) => {
    formik.setValues({ ...formik.values, [key]: "" });
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const renderErrors = () => {
    if (formik.errors.unknown) {
      return formik.errors.unknown as string;
    }

    if (formik.errors) {
      return Object.values(formik.errors)[0];
    }

    return "";
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();

    if (
      formik.values.username &&
      formik.values.username.length > 0 &&
      formik.values.password &&
      formik.values.password.length
    ) {
      formik.handleSubmit(event);
    }
  };

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    onSubmit: async (
      values: AdminLoginRequestAttributes,
      { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        const deviceInfos = deviceInfo();
        const requestData = {
          device_name: deviceInfos.device_name,
          device_model: deviceInfos.device_model,
          ...values,
        };
        const response = await UserService.adminLogin(requestData);
        formik.values.password = "";

        if (response.isSuccess) {
          switch (response.data.code) {
            case 150: // Validation error
              setErrors(parseJson(response.data.data));
              setSubmitting(false);
              break;
            case 100: // Ok
              // Get token and get user information
              let token = parseJson(response.data.data);
              // Get user info
              let user: User = await remoteAuthUserRequest(
                  token.access_token,
                  authDispatch
              );
              setSubmitting(false);

              if (user !== null) {
                history.replace(ICIPAdminScreen.routeName);
              }
              break;
            default:
              // Error
              setErrors({ unknown: response.data.message });
              setSubmitting(false);
              break;
          }
          return;
        }
      } catch (err) {
        console.dir(err);
      }

      setSubmitting(false);
      setErrors({
        unknown: intl.formatMessage({ id: "error.unknownError" }),
      });
    },
  });

  if (Boolean(_t !== null) && Boolean(_u !== null)) {
    return <Redirect to={{ pathname: ICIPAdminScreen.routeName }} />;
  }

  return (
    <>
      <PageTitle
          withAppTitle={false}
          title={intl.formatMessage({ id: "sharedScreen.adminLogin" })}
      />
      <Box className={classes.root}>
        <Box className={classes.wrapper}>
          <Box mb={1} className={classes.titleWrapper}>
            <Link to={SignInScreen.routeName}>
              <Typography noWrap component="span">
                {APP_NAME}
              </Typography>
              <Typography noWrap component="span" className="extra">
                Admin.
              </Typography>
            </Link>
          </Box>
          <Box mb={4}>
            <Typography component="p" className={classes.caption}>
              <FormattedMessage id="sharedScreen.adminLoginNotice" />
            </Typography>
          </Box>
          <SlideWrapper>
            <form noValidate autoComplete="off" onSubmit={handleSubmit}>
              <Box className={classes.inputWrapper}>
                <InputStyled
                    id="username"
                    name="username"
                    disabled={formik.isSubmitting}
                    placeholder={intl.formatMessage({
                      id: "sharedScreen.emailAddressOrUsername",
                    })}
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    endAdornment={
                      formik.values.username &&
                      formik.values.username.length > 0 && !formik.isSubmitting ? (
                          <InputAdornment>
                            <StyledIconButton
                                aria-label="clear input visibility"
                                onClick={() => handleClearInput("username")}
                            >
                              <ClearIcon fontSize="inherit" />
                            </StyledIconButton>
                          </InputAdornment>
                      ) : (
                          <></>
                      )
                    }
                />
              </Box>
              <Box className={classes.inputWrapper}>
                <InputStyled
                    id="password"
                    name="password"
                    disabled={formik.isSubmitting}
                    placeholder={intl.formatMessage({
                      id: "sharedScreen.yourPassword",
                    })}
                    type={values.showPassword ? "text" : "password"}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    endAdornment={
                      formik.values.password &&
                      formik.values.password.length > 0 && !formik.isSubmitting ? (
                          <InputAdornment>
                            <StyledIconButton
                                aria-label="clear input visibility"
                                onClick={() => handleClearInput("password")}
                            >
                              <ClearIcon fontSize="inherit" />
                            </StyledIconButton>
                          </InputAdornment>
                      ) : (
                          <> </>
                      )
                    }
                />
                <Box className={classes.flexLabel} mt={1}>
                  <Box style={{ flexGrow: 1 }} component="span" />
                  <Typography
                      noWrap
                      component="span"
                      className={classes.linkAction}
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                  >
                    {values.showPassword ? (
                        <FormattedMessage id="sharedScreen.hidePassword" />
                    ) : (
                        <FormattedMessage id="sharedScreen.showPassword" />
                    )}
                  </Typography>
                </Box>
              </Box>
              <Box mt={2} className={classes.loginButtonWrapper}>
                <LoginButton fullWidth disabled={formik.isSubmitting} type="submit">
                  <Typography component="span" noWrap className={classes.buttonLabel}>
                    {formik.isSubmitting ? (
                        <FormattedMessage id="sharedScreen.connecting" />
                    ) : (
                        <FormattedMessage id="sharedScreen.signIn" />
                    )}
                  </Typography>
                  {formik.isSubmitting ? (
                      <CircularProgress color="inherit" size={16} thickness={4} className="loader" />
                  ) : (
                      <></>
                  )}
                </LoginButton>
              </Box>
              <Box mt={3} style={{ textAlign: "center" }}>
                <Typography component="span" color="error" variant="h6">
                  {renderErrors()}
                </Typography>
              </Box>
            </form>
          </SlideWrapper>
          <LanguageChooser />
        </Box>
      </Box>
    </>
  );
};

AdminSignIn.routeName = "/admin/v2/sign-in";

export default AdminSignIn;
